import React from 'react';
import DOMPurify from 'dompurify';
import FileUploader from './FileUploader';
import validators from '../../../utils/forms/validators';

const {sanitize} = DOMPurify;

export class Field extends React.PureComponent {
	render() {
		return (
			<div
				className={`field ${this.props.className || ''} ${
					this.props.addOns && this.props.addOns.length ? 'has-addons' : ''
				}`}
			>
				{this.props.children}
			</div>
		);
	}
}

export class Control extends React.PureComponent {
	render() {
		return (
			<div className={`control ${this.props.controlClasses || ''}`}>
				{this.props.children}
			</div>
		);
	}
}

export class Label extends React.PureComponent {
	render() {
		return this.props.label ? (
			<div className="pb-1">
				<label
					htmlFor={this.props.dataKey || this.props.label}
					className={`label is-size-6 has-text-primary ${
						this.props.labelClasses || ''
					}`}
					style={this.props.type == 'checkbox' ? { marginTop: '-5px' } : {}}
				>
					{this.props.label}
					{this.props.subLabel &&
						(React.isValidElement(this.props.subLabel) ? (
							<div className="label is-size-7 has-text-grey mt-0">
								{this.props.subLabel}
							</div>
						) : (
							<div
								className="label is-size-7 has-text-grey mt-0"
								dangerouslySetInnerHTML={{
									__html: sanitize(this.props.subLabel),
								}}
							/>
						))}
				</label>
			</div>
		) : (
			<></>
		);
	}
}

class ErrorMessage extends React.PureComponent {}

export default class Input extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			hasSuccess: false,
			isFocused: false,
			passwordIsVisible: false,
			newsRows: this.parseNewsValue(props.value), // news links type
		};

		this.validatorTimeout = null;
	}

	static VALIDATION_TIMEOUT_LENGTH = 800;

	static VALIDATE_ON_MOUNT_TIMEOUT = 1000;

	// Fields for news links type
	parseNewsValue(val) {
		if (!val || typeof val !== 'string') {
		  return [{ id: this.genId(), text: '', url: '', date: '' }];
		}
	  
		// Normaliza y quita comillas envolventes si vienen del backend:  "texto|url|fecha; texto2|url2|fecha2"
		let s = val.trim();
		if (s.startsWith('"') && s.endsWith('"')) {
		  s = s.slice(1, -1);
		}
	  
		// Divide por ';' y limpia vacíos
		const parts = s.split(';').map(p => p.trim()).filter(Boolean);
	  
		const rows = parts.map(part => {
		  const pieces = part.split('|').map(p => p.trim());
		  let text = '';
		  let url  = '';
		  let date = '';

		  if (pieces.length === 1) {
			if (/^https?:\/\//i.test(pieces[0])) {
			  url = pieces[0];
			} else {
			  text = pieces[0];
			}
		  } else if (pieces.length === 2) {
			text = pieces[0];
			url  = pieces[1];
		  } else {
			text = pieces[0];
			url  = pieces[1];
			date = pieces[2];
		  }
	  
		  return { id: this.genId(), text, url, date };
		});
	  
		return rows.length ? rows : [{ id: this.genId(), text: '', url: '', date: '' }];
	  }
	  
	serializeNewsRows(rows) {
		const scrub = (str = '') =>
		  String(str).trim()
			.replace(/;/g, '﹔')   // reemplaza ; por un lookalike para no romper el split
			.replace(/\|/g, '｜'); // reemplaza | por lookalike
	  
		const cleaned = rows
		  .map(r => ({
			text: scrub(r.text),
			url:  String(r.url || '').trim(),
			date: String(r.date || '').trim()
		  }))
		  .filter(r => r.text || r.url || r.date); // evita filas totalmente vacías
	  
		return cleaned.map(r => (r.date ? `${r.text}|${r.url}|${r.date}` : `${r.text}|${r.url}`)).join('; ');
	  }
	  
	handleNewsRowChange(i, key, val) {
	const rows = [...this.state.newsRows];
	rows[i] = { ...rows[i], [key]: val };
	this.setState({ newsRows: rows }, () => {
		this.props.onChange && this.props.onChange(this.serializeNewsRows(this.state.newsRows));
	});
	}
	
	addNewsRow() {
	const newRow = { id: this.genId(), text: '', url: '', date: '' };
	const rows = [...this.state.newsRows, newRow];
	this.setState({ newsRows: rows }, () => {
		this.props.onChange && this.props.onChange(this.serializeNewsRows(this.state.newsRows));
	});
	}
	
	removeNewsRow(i) {
	const rows = this.state.newsRows.filter((_, idx) => idx !== i);
	const fallback = [{ id: this.genId(), text: '', url: '', date: '' }];
	this.setState({ newsRows: rows.length ? rows : fallback }, () => {
		this.props.onChange && this.props.onChange(this.serializeNewsRows(this.state.newsRows));
	});
	}
	/* end functions for news links type */

	componentDidMount() {
		if (this.props.validator && this.props.validateOnMount) {
			this.validatorTimeout = setTimeout(() => {
				if (this.props.validator(this.props.value)) {
					this.setState({ hasSuccess: true });
					this.props.onSuccess && this.props.onSuccess();
				}
			}, Input.VALIDATE_ON_MOUNT_TIMEOUT);
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.value !== this.props.value) {
		  const incoming = this.parseNewsValue(this.props.value);
		  const incomingSerialized = this.serializeNewsRows(incoming);
		  const currentSerialized  = this.serializeNewsRows(this.state.newsRows);
		  if (incomingSerialized !== currentSerialized) {
			this.setState({ newsRows: incoming });
		  }
		}
	}

	// need to be able to force error from props.
	hasError = () => {
		return this.props.hasError || this.state.hasError;
	};

	handleChange = (ev) => {
		let val;
		if (ev.target.type === 'checkbox') {
			val = ev.target.checked;
		} else {
			val = ev.target.value;
		}

		if (this.state.hasError || this.state.hasSuccess) {
			this.setState({ hasError: false, hasSuccess: false });
		}
		// handle passed changes based on pass function
		if (this.props.onChange) this.props.onChange(val);

		// handle validation if function passed
		if (this.props.validator) {
			clearTimeout(this.validatorTimeout);
			this.validatorTimeout = setTimeout(() => {
				if (!this.props.validator(val)) {
					this.setState({ hasError: true });
					if (this.props.onError) this.props.onError();
				} else {
					this.setState({ hasSuccess: true });
					this.props.onSuccess && this.props.onSuccess();
				}
			}, Input.VALIDATION_TIMEOUT_LENGTH);
		}
	};

	onFocus = (e) => {
		this.setState({ isFocused: true });
	};

	onBlur = (e) => {
		this.setState({ isFocused: false });
	};

	renderOptions() {
		if (!this.props.options || !Array.isArray(this.props.options)) return [];
		let options = this.props.options.reduce((r, d, i, a) => {
			r.push(
				<option value={d.value} key={d.value} disabled={!!d.disabled} style={{ color: '#363636' }}>
					{d.display}
				</option>
			);
			return r;
		}, []);

		options.unshift(
			<option
				value=""
				key="placeholder"
				disabled
				className="has-text-grey-light"
				style={{ color: '#7a7a7a' }}
				selected
			>
				{this.props.placeholder || '--'}
			</option>
		);

		return options;
	}

	getInputClassNames = () => {
		return `${this.props.inputClasses || ''} ${
			this.state.hasSuccess ? 'is-success' : ''
		} ${this.hasError() ? 'is-danger' : ''}`;
	};

	// allows for the application of a formatter without altering the props value on TEXT FIELDS ONL.
	getFormattedValue = () => {
		if (this.props.formatter && !this.state.isFocused) {
			return this.props.formatter(this.props.value);
		}
		return this.props.value;
	};

	genId = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

	defaultNewsRows = [
		{ id: this.genId(), text: "Learn about PACE", url: "https://blog.cleanfi.com/capitalized-interest-as-an-element-of-pace-financing/" }
	];

	render() {
		let { type, className } = this.props;
		switch (type) {
			case 'news_links':
				return (
					<Field {...this.props}>
					<Label {...this.props} />

					<div className="box">
						{this.state.newsRows.map((row, i) => (
						<div key={row.id} className="columns is-mobile is-vcentered mb-2">
							<div className="column">
							<input
								className={'input ' + this.getInputClassNames()}
								type="text"
								placeholder="The New text. Ex: 'Now you can download CSV'"
								value={row.text}
								onChange={(e) => this.handleNewsRowChange(i, 'text', e.target.value)}
								aria-label={`news-text-${i}`}
							/>
							</div>
							<div className="column">
							<input
								className={'input ' + this.getInputClassNames()}
								type="url"
								placeholder="https://new_link.com"
								value={row.url}
								onChange={(e) => this.handleNewsRowChange(i, 'url', e.target.value)}
								aria-label={`news-url-${i}`}
							/>
							</div>
							<div className="column is-narrow">
							<input
								className={'input ' + this.getInputClassNames()}
								type="date"
								value={row.date || ''}
								onChange={(e) => this.handleNewsRowChange(i, 'date', e.target.value)}
								aria-label={`news-date-${i}`}
							/>
							</div>
							<div className="column is-narrow">
							<button
								type="button"
								className="button is-light"
								onClick={() => this.removeNewsRow(i)}
								title="Delete new"
							>
								✕
							</button>
							</div>
						</div>
						))}

						<button type="button" className="button is-link is-light" onClick={() => this.addNewsRow()}>
						+ Add New
						</button>
					</div>

					{!!this.props.addOns && this.props.addOns}
					{!!this.hasError() && <p className="help is-danger">{this.props.errorText}</p>}
					</Field>
				);
			case 'textarea':
				return (
					<Field {...this.props}>
						<Control {...this.props}>
							<Label {...this.props} />
							<textarea
								className={`textarea ${this.getInputClassNames()}`}
								id={this.props.dataKey}
								aria-label={this.props.label}
								onChange={this.handleChange}
								value={this.props.value}
								rows={this.props.rows || 2}
								placeholder={this.props.placeholder || '...'}
							/>
						</Control>
					</Field>
				);

			case 'select':
				const isPlaceholderSelected = !this.props.value && this.props.value !== 0;
				const selectStyle = isPlaceholderSelected ? { color: '#7a7a7a' } : { color: '#363636' };
				return (
					<Field {...this.props}>
						<Label {...this.props} />
						<Control {...this.props}>
							<div className={`select ${this.getInputClassNames()} `}>
								<select
									className={this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									onChange={this.handleChange}
									value={this.props.value}
									style={selectStyle}
								>
									{this.renderOptions()}
								</select>
							</div>
						</Control>
						{!!this.props.addOns && this.props.addOns}
						{!!this.hasError() && (
							<p className="help is-danger">{this.props.errorText}</p>
						)}
					</Field>
				);

			case 'checkbox':
				return (
					<Field {...this.props}>
						<div className="is-flex">
							<input
								type="checkbox"
								id={this.props.dataKey}
								aria-label={this.props.label}
								className={`mr-3 ${this.getInputClassNames()}`}
								onChange={this.handleChange}
								checked={
									[null, undefined].includes(this.props.value)
										? this.props.defaultChecked || false
										: this.props.value
								}
								defaultChecked={this.props.defaultChecked || false}
							/>
							<Label {...this.props} />
						</div>
						{!!this.props.addOns && this.props.addOns}
						{!!this.hasError() && (
							<p className="help is-danger">{this.props.errorText}</p>
						)}
					</Field>
				);

			case 'password':
				return (
					<Field {...this.props}>
						<Label {...this.props} />
						<Control
							{...this.props}
							controlClasses={'has-icons-right ' + this.props.controlClasses}
						>
							<input
								className={'input ' + this.getInputClassNames()}
								id={this.props.dataKey}
								aria-label={this.props.label}
								type={this.state.passwordIsVisible ? 'text' : 'password'}
								onChange={this.handleChange}
								value={this.props.value || ''}
								placeholder={this.props.placeholder}
								onBlur={this.props.onBlur || (() => null)}
							/>
							<span
								className="icon is-right is-clickable"
								onClick={() => {
									this.setState({
										passwordIsVisible: !this.state.passwordIsVisible,
									});
								}}
							>
								<i
									className={`far ${
										this.state.passwordIsVisible ? 'fa-eye-slash' : 'fa-eye'
									}`}
								/>
							</span>
						</Control>
						{!!this.props.addOns && this.props.addOns}
						{!!this.hasError() && (
							<p className="help is-danger">{this.props.errorText}</p>
						)}
					</Field>
				);

			case 'phone':
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control
								{...this.props}
								style={{ display: 'flex', width: '100%' }}
							>
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="text"
									onChange={(e) => {
										e.target.value = e.target.value.replace(/[^\d\s()+-]/g, '');
										this.handleChange(e);
									}}
									value={this.getFormattedValue() || ''}
									placeholder={this.props.placeholder}
									disabled={this.props.disabled || false}
								/>
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				);

			case 'file':
				return <FileUploader {...this.props} />;

			case 'date':
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control
								{...this.props}
								style={
									this.props.addOns && this.props.addOns.length
										? {
												display: 'flex',
												width: '100%',
										  }
										: {}
								}
							>
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="date"
									onChange={this.handleChange}
									value={this.getFormattedValue() || new Date()}
									placeholder={this.props.placeholder}
								/>
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				);

			case 'hex_color':
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control {...this.props} controlClasses="has-icons-right">
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="text"
									onChange={this.handleChange}
									value={this.getFormattedValue() || ''}
									placeholder={this.props.placeholder}
								/>
								<span className="icon is-right">
									<i
										className="fas fa-square fa-2x"
										style={{ color: this.getFormattedValue() }}
									/>
								</span>
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				);

			case 'text':
			default:
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control
								{...this.props}
								style={{ display: 'flex', width: '100%' }}
							>
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="text"
									onChange={this.handleChange}
									value={this.getFormattedValue() || ''}
									placeholder={this.props.placeholder}
									disabled={this.props.disabled || false}
								/>
								{validators.isValidHex.validator(this.getFormattedValue()) && (
									<div
										style={{
											width: '20px',
											height: '20px',
											backgroundColor: this.getFormattedValue(),
											display: 'inline-block',
											marginLeft: '10px',
										}}
									/>
								)}
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				);
		}
	}
}
